import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";

import {
  GET_MATRIX_WEEK,
  GET_MATRIX_DAY_LOCKS,
  GET_MATRIX_WEEK_LOCKS,
  LOCK_DRIVER_PROFILE,
  UNLOCK_DRIVER_PROFILE,
  LOCK_DRIVER_DAY,
  UNLOCK_DRIVER_DAY,
  LOCK_ROUTE_DAY,
  UNLOCK_ROUTE_DAY,
  UPDATE_MATRIX_WEEK,
  ADD_DRIVER_PROFILE,
  UPDATE_DRIVER_PROFILE,
  REMOVE_DRIVER_PROFILE,
  GET_WEEK_SECTIONS,
  GET_VEHILCES,
  FORCE_UNLOCK_DEPOT_DRIVERS,
  FORCE_UNLOCK_MATRIX_WEEK,
  GET_COUNTRIES,
  GET_DRIVER_PROFILE_IMAGE,
  VALIDATE_DRIVER_PROFILE_IMAGE,
} from "../../consts/apis";
import {
  SUCCESS_FETCH_DEPOT_VEHICLES,
  ERROR_FETCH_DEPOT_VEHICLES,
  SUCCESS_FETCH_DEPOT_SECTIONS,
  ERROR_FETCH_DEPOT_SECTIONS,
  SUCCESS_FETCH_DEPOT_MATRIX,
  ERROR_FETCH_DEPOT_MATRIX,
  SUCCESS_UNLOCK_DEPOT_DRIVERS,
  SUCCESS_UNLOCK_MATRIX_WEEK,
  SUCCESS_FETCH_COUNTRY_CODES,
  ERROR_FETCH_COUNTRY_CODES,
  SUCCESS_FETCH_DRIVER_IMAGE,
  ERROR_FETCH_DRIVER_IMAGE,
  SUCCESS_VALIDATE_DRIVER_IMAGE,
  ERROR_VALIDATE_DRIVER_IMAGE,
} from "../../consts/messages";
import {
  DRIVER_DAY_FAILED_TO_LOCK,
  DRIVER_DAY_FAILED_TO_UNLOCK,
  DRIVER_DAY_SUCESSFULLY_LOCKED,
  DRIVER_DAY_SUCESSFULLY_UNLOCKED,
  DRIVER_PROFILE_FAILED_TO_LOCK,
  DRIVER_PROFILE_FAILED_TO_UNLOCK,
  DRIVER_PROFILE_SUCESSFULLY_LOCKED,
  DRIVER_PROFILE_SUCESSFULLY_UNLOCKED,
  DRIVER_FORCE_UNLOCK_FAILED,
  ROUTE_DAY_FAILED_TO_LOCK,
  ROUTE_DAY_FAILED_TO_UNLOCK,
  ROUTE_DAY_SUCESSFULLY_LOCKED,
  ROUTE_DAY_SUCESSFULLY_UNLOCKED,
  ROUTE_DAY_NOT_SUITABLE_WARNING,
} from "../../consts/strings";
import {
  DRIVER_DAY_CHANGED,
  DRIVER_DAY_LOCKED,
  DRIVER_DAY_LOCKED_IN_OTHER_BROWSER,
  DRIVER_PROFILE_CHANGED,
  DRIVER_PROFILE_LOCKED,
  DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY,
  DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER,
  MATRIX_TEMPLATE_USER_SESSION_CHANGED,
  ROUTE_DAY_CHANGED,
  ROUTE_DAY_LOCKED,
  ROUTE_DAY_LOCKED_IN_OTHER_BROWSER,
} from "../../consts/errorCodes";
import { CHOICE_PLAN_TYPE, ROBOTS_PLAN_TYPE } from "../../consts/planTypes";
import {
  CHOICE_DRIVER_TYPE,
  DR_DRIVER_TYPE,
  ODF_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../consts/driverTypes";
import { ACTIVE, ALL } from "../../consts/driverDetails";
import { POSTCODE } from "../../consts/layers";

import api from "../../services/api";

import {
  deleteDriverManagementDriverDayChecksum,
  deleteDriverManagementDriverProfileChecksum,
  deleteDriverManagementRouteDayChecksum,
  processDriverManagementLocks,
  processDriverManagementWeekLocksRefresh,
  processDriverManagementDayLocksRefresh,
  saveDriverManagementDriverDayChecksum,
  saveDriverManagementDriverProfileChecksum,
  saveDriverManagementRouteDayChecksum,
  deleteDriverManagementDepotDriverDayChecksums,
  deleteDriverManagementDepotRouteDayChecksums,
  deleteDriverManagementDepotDriverProfileChecksums,
} from "../../utils/locks";
import {
  applyChangeToMatrixDay,
  generateDriverColors,
  generateDriverDaysConfig,
  generateDriverProfilesConfig,
  getDriverDayEdits,
  getUpdateImpactOnMatrixDay,
  updateDriverDay,
  updateDriverProfile,
} from "../../utils/drivers";
import {
  generateRouteDaysConfig,
  isNonRedirectionRouteCode,
} from "../../utils/routes";
import { syncToAsync } from "../../utils/async";

import {
  driverManagementSessionChecksumSelector,
  userSessionExpiredSelector,
  locksRefreshRequiredSelector,
  driverProfileSelector,
  driverProfileLockSelector,
  driverProfileLocksSelector,
  driverDayLockSelector,
  routeDayLockSelector,
  matrixDayDriversSelector,
  driverDayEditSelector,
  driverDayEditCopySelector,
  matrixDaySelector,
  driverProfilesSelector,
  routeDayLocksSelector,
  driverDayLocksSelector,
  matrixDayRoutesSelector,
  driverEditSelector,
  driverEditCopySelector,
  matrixDaysSelector,
  driversImpactedSelector,
  driverWeekLocksSelector,
  driverDayEditsSelector,
  driverImageSelector,
  driverViewSelector,
  selectingDriverLocationSelector,
  newDriverProfileEditSelector,
  driverProfileEditSelector,
} from "./selectors";
import {
  addNotification,
  setDriverManagementExpiredModalOpen,
  setSelectedDriver,
  setShowReloadDriverManagement,
} from "../ownApp";
import { getUserMetadata } from "../ownUser/selectors";
import { getSelectedDepot } from "../ownApp/selectors";

export const resetDriverManagement = createAsyncThunk(
  "driverManagement/resetDriverManagement",
  async (params) => ({})
);

export const setDriverView = createAction("driverManagement/setDriverView");

export const setHideDriverManagementModal = createAction(
  "driverManagement/setHideDriverManagementModal"
);

export const setSelectingDriverLocation = createAction(
  "driverManagement/setSelectingDriverLocation"
);

export const setNewDriverEdit = createAction(
  "driverManagement/setNewDriverEdit"
);

export const setNewDriverUsers = createAction(
  "driverManagement/setNewDriverUsers"
);

export const setNewDriverProfileEdit = createAction(
  "driverManagement/setNewDriverProfileEdit"
);

export const setDriverLocationCoordinates = createAsyncThunk(
  "driverManagement/setDriverLocationCoordinates",
  async ({ postcode, level, lngLat }, { dispatch, getState }) => {
    const state = getState();
    const selectingDriverLocation = selectingDriverLocationSelector(state);
    const newDriverProfileEdit = newDriverProfileEditSelector(state);
    const driverProfileEdit = driverProfileEditSelector(state);

    if (level !== POSTCODE) {
      return dispatch(
        addNotification({
          message: "Zoom in to allocate driver location postcode",
          options: { variant: "info" },
        })
      );
    }

    if (newDriverProfileEdit)
      dispatch(
        setNewDriverProfileEdit({
          ...newDriverProfileEdit,
          [`${selectingDriverLocation}Postcode`]: postcode,
          [`${selectingDriverLocation}Latitude`]: lngLat?.lat || null,
          [`${selectingDriverLocation}Longitude`]: lngLat?.lng || null,
        })
      );
    else if (driverProfileEdit)
      dispatch(
        updateDriverProfileEdit({
          updates: [
            { name: `${selectingDriverLocation}Postcode`, value: postcode },
            {
              name: `${selectingDriverLocation}Latitude`,
              value: lngLat?.lat || null,
            },
            {
              name: `${selectingDriverLocation}Longitude`,
              value: lngLat?.lng || null,
            },
          ],
        })
      );
  }
);
export const fetchVehicles = createAsyncThunk(
  "driverManagement/fetchVehicles",
  async (params, { dispatch }) => {
    try {
      const {
        data: { vehicles },
      } = await api.get(GET_VEHILCES());

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_VEHICLES,
          options: { variant: "success" },
        })
      );

      return vehicles;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_VEHICLES,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchCountries = createAsyncThunk(
  "driverManagement/fetchCountries",
  async (params, { dispatch }) => {
    try {
      const {
        data: { countries },
      } = await api.get(GET_COUNTRIES());

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_COUNTRY_CODES,
          options: { variant: "success" },
        })
      );

      return countries;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_COUNTRY_CODES,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchDepotSectionWeek = createAsyncThunk(
  "driverManagement/fetchDepotSectionWeek",
  async ({ depotCode }, { dispatch }) => {
    try {
      const {
        data: { sectionDays },
      } = await api.get(GET_WEEK_SECTIONS(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_SECTIONS,
          options: { variant: "success" },
        })
      );

      return sectionDays;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_SECTIONS,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchDepotMatrixWeek = createAsyncThunk(
  "driverManagement/fetchDepotMatrixWeek",
  async ({ depotCode }, { dispatch, getState }) => {
    const state = getState();
    const {
      user: { user_id: userCode },
    } = getUserMetadata(state);

    try {
      const {
        data: {
          sessionChecksum,
          driverProfiles,
          driverProfileLocks,
          matrixDays,
        },
      } = await api.get(GET_MATRIX_WEEK(depotCode, "DRIVER_MANAGEMENT"));

      processDriverManagementLocks({
        userCode,
        depotCode,
        driverProfileLocks,
        matrixDays,
      });

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_MATRIX,
          options: { variant: "success" },
        })
      );

      dispatch(setSelectedDriver(null));
      return {
        sessionChecksum,
        driverProfiles: generateDriverColors({ driverProfiles }),
        driverProfileLocks,
        matrixDays,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_MATRIX,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const refreshDepotMatrixWeekLocks = createAsyncThunk(
  "driverManagement/refreshDepotMatrixWeekLocks",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const {
      user: { user_id: userCode },
    } = getUserMetadata(state);

    const depotCode = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLocks = driverProfileLocksSelector(state);
    const matrixDays = matrixDaysSelector(state);

    try {
      const {
        data: {
          driverProfileLocks: newDriverProfileLocks,
          matrixDaysLocks: newMatrixDays,
        },
      } = await api.get(GET_MATRIX_WEEK_LOCKS(depotCode, sessionChecksum));

      const {
        refreshedDriverProfileLocks,
        refreshedMatrixDays,
        hasChangedChecksum,
      } = processDriverManagementWeekLocksRefresh({
        userCode,
        depotCode,
        driverProfileLocks,
        matrixDays,
        newDriverProfileLocks,
        newMatrixDays,
      });

      if (hasChangedChecksum) dispatch(setShowReloadDriverManagement(true));

      return {
        driverProfileLocks: refreshedDriverProfileLocks,
        matrixDays: refreshedMatrixDays,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: "Fail to refresh depot locks",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const refreshDepotMatrixDayLocks = createAsyncThunk(
  "driverManagement/refreshDepotMatrixDayLocks",
  async ({ dayOfWeek }, { dispatch, getState }) => {
    const state = getState();
    const {
      user: { user_id: userCode },
    } = getUserMetadata(state);

    const depotCode = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const matrixDays = matrixDaysSelector(state);

    try {
      const {
        data: {
          driverDayLocks: newDriverDayLocks,
          routeDayLocks: newRouteDayLocks,
        },
      } = await api.get(
        GET_MATRIX_DAY_LOCKS(depotCode, dayOfWeek, sessionChecksum)
      );

      const { refreshedMatrixDays, hasChangedChecksum } =
        processDriverManagementDayLocksRefresh({
          userCode,
          depotCode,
          dayOfWeek,
          matrixDays,
          newDriverDayLocks,
          newRouteDayLocks,
        });

      if (hasChangedChecksum) dispatch(setShowReloadDriverManagement(true));

      return { dayOfWeek, matrixDays: refreshedMatrixDays };
    } catch (error) {
      dispatch(
        addNotification({
          message: "Fail to refresh depot locks",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const setUserSessionExpired = createAction(
  "driverManagement/setUserSessionExpired"
);

export const setLocksRefreshRequired = createAction(
  "driverManagement/setLocksRefreshRequired"
);

export const fetchDriverImage = createAsyncThunk(
  "driverManagement/fetchDriverImage",
  async ({ driverCode }, { dispatch, getState }) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);

    let image = null;
    if (driverProfile.photoCode) {
      try {
        ({
          data: { image },
        } = await api.get(GET_DRIVER_PROFILE_IMAGE(driverProfile.photoCode)));

        dispatch(
          addNotification({
            message: SUCCESS_FETCH_DRIVER_IMAGE,
            options: { variant: "success" },
          })
        );
      } catch (error) {
        dispatch(
          addNotification({
            message: ERROR_FETCH_DRIVER_IMAGE,
            options: { variant: "error" },
          })
        );
        throw error;
      }
    } else throw new Error("No photo code");
    return { driverCode, image };
  }
);

export const updateDriverImage = createAsyncThunk(
  "driverManagement/updateDriverImage",
  async ({ image }, { dispatch, getState }) => {
    const state = getState();
    const driverEdit = driverEditSelector(state);
    let { photoCode } = driverEdit.driverProfileEdit;

    if (!photoCode) photoCode = uuidv4();
    const { error } = await dispatch(
      updateDriverProfileEdit({
        updates: [
          {
            name: "photoCode",
            value: photoCode,
          },
        ],
        keepLoading: true,
      })
    );
    if (error) throw error;

    try {
      const {
        data: { success },
      } = await api.post(VALIDATE_DRIVER_PROFILE_IMAGE(), null, { image });

      if (!success) throw new Error(ERROR_VALIDATE_DRIVER_IMAGE);
      else
        dispatch(
          addNotification({
            message: SUCCESS_VALIDATE_DRIVER_IMAGE,
            options: { variant: "success" },
          })
        );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
    return { image };
  }
);

export const addDriver = createAsyncThunk(
  "driverManagement/addDriver",
  async ({ user, driverProfile }, { dispatch, getState }) => {
    const state = getState();
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfiles = driverProfilesSelector(state);
    const driverProfileLocks = driverProfileLocksSelector(state);
    const matrixDays = matrixDaysSelector(state);

    try {
      const { data } = await api.post(
        ADD_DRIVER_PROFILE(driverProfile.driverCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT",
          sessionChecksum,
          user,
          driverProfile,
        }
      );
      if (!data.success) throw new Error("Failed to add new driver");

      dispatch(
        addNotification({
          message: `Successfully saved new driver profile.`,
          options: { variant: "success" },
        })
      );
      const payload = {
        driverProfiles: {
          ...driverProfiles,
          [data.driverProfile.driverCode]: data.driverProfile,
        },
        driverProfileLocks: {
          ...driverProfileLocks,
          [data.driverProfile.driverCode]: {
            ...data.driverProfileLock,
            lockedByYou: false,
            lockedInDifferentBrowser: false,
          },
        },
        matrixDays: [0, 1, 2, 3, 4, 5, 6].reduce((acc, dayOfWeek) => {
          acc[dayOfWeek] = {
            ...matrixDays[dayOfWeek],
            drivers: {
              ...matrixDays[dayOfWeek].drivers,
              [data.driverProfile.driverCode]: data.driverDays[dayOfWeek],
            },
            driverDayLocks: {
              ...matrixDays[dayOfWeek].driverDayLocks,
              [data.driverProfile.driverCode]: data.driverDayLocks[dayOfWeek],
            },
          };
          return acc;
        }, {}),
      };

      syncToAsync(() => {
        const driverView = driverViewSelector(getState());
        if (driverView === ACTIVE) dispatch(setDriverView(ALL));
        dispatch(
          setSelectedDriver({
            driverCode: data.driverProfile.driverCode,
            driverProfileEdit: { ...data.driverProfile },
            driverDayEdits: getDriverDayEdits({
              driverCode: data.driverProfile.driverCode,
              matrixDays: payload.matrixDays,
            }),
          })
        );
      });

      return payload;
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const removeDriver = createAsyncThunk(
  "driverManagement/removeDriver",
  async ({ driverCode }, { dispatch, getState }) => {
    const { error } = await dispatch(editDriver({ driverCode }));
    if (error) throw error;

    const state = getState();
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const selectedDepot = getSelectedDepot(state);
    const driverEdit = driverEditSelector(state);
    const { driverProfileEdit, driverDayEdits } = driverEdit;

    try {
      const { error } = await dispatch(lockDriverProfile({ driverCode }));
      if (error) throw error;

      // Unallocates all currently allocated routes to driver.
      // Must be done in serial, do not change to parallel
      for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
        if (driverDayEdits[dayOfWeek].routeCode) {
          const { error } = await dispatch(
            updateDriverDayEdit({
              dayOfWeek,
              name: "routeCode",
              value: null,
              keepLoading: true,
            })
          );
          if (error) throw error;
        }

      // Removes all currently planned ODF-DR drivers relations.
      if (driverProfileEdit.driverType === ODF_DRIVER_TYPE) {
        // Must be done in serial, do not change to parallel
        for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
          if (driverDayEdits[dayOfWeek].diverCode !== driverCode) {
            const { error } = await dispatch(
              updateDriverDayEdit({
                dayOfWeek,
                name: "driverCode",
                value: driverCode,
                keepLoading: true,
              })
            );
            if (error) throw error;
          }
      } else if (driverProfileEdit.driverType === DR_DRIVER_TYPE) {
        // Must be done in serial, do not change to parallel
        for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
          if (driverDayEdits[dayOfWeek].plannedOdf !== driverCode) {
            const { error } = await dispatch(
              updateDriverDayEdit({
                dayOfWeek,
                name: "plannedOdf",
                value: null,
                keepLoading: true,
              })
            );
            if (error) throw error;
          }
      }

      const stateAfterUpdates = getState();
      const driverProfiles = driverProfilesSelector(stateAfterUpdates);
      const driverProfileLocks = driverProfileLocksSelector(stateAfterUpdates);

      const newDriverProfiles = { ...driverProfiles };
      delete newDriverProfiles[driverCode];
      const { newMatrixDays, matrixDaysUpdate } = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          const { drivers, routes, driverDayLocks, routeDayLocks } =
            matrixDaySelector(dayOfWeek)(stateAfterUpdates);
          const driverEdit =
            driverDayEditSelector(dayOfWeek)(stateAfterUpdates);
          const driverEditCopy =
            driverDayEditCopySelector(dayOfWeek)(stateAfterUpdates);

          if (driverEdit !== driverEditCopy) {
            const {
              impactedRoutes,
              impactedDrivers,
              routes: newRoutes,
              drivers: newDrivers,
            } = applyChangeToMatrixDay({
              dayOfWeek,
              driverEdit,
              driverEditCopy,
              drivers,
              routes,
            });

            acc.newMatrixDays[dayOfWeek] = {
              routes: newRoutes,
              drivers: newDrivers,
              driverDayLocks: { ...driverDayLocks },
              routeDayLocks,
            };
            acc.matrixDaysUpdate[dayOfWeek] = {
              hasChanges: true,
              routeDays: generateRouteDaysConfig({
                routes: impactedRoutes,
                routeDayLocks,
              }),
              drivers: generateDriverProfilesConfig({
                drivers: impactedDrivers,
                driverProfileLocks,
              }).filter(
                ({ driverCode: updateDriverCode }) =>
                  updateDriverCode !== driverCode
              ),
              driverDays: generateDriverDaysConfig({
                drivers: impactedDrivers,
                driverDayLocks,
              }).filter(
                ({ driverCode: updateDriverCode }) =>
                  updateDriverCode !== driverCode
              ),
            };
          } else {
            acc.newMatrixDays[dayOfWeek] = {
              routes,
              drivers: { ...drivers },
              driverDayLocks: { ...driverDayLocks },
              routeDayLocks,
            };
            acc.matrixDaysUpdate[dayOfWeek] = {
              hasChanges: false,
              routeDays: [],
              drivers: [],
              driverDays: [],
            };
          }
          delete acc.newMatrixDays[dayOfWeek].drivers[driverCode];
          delete acc.newMatrixDays[dayOfWeek].driverDayLocks[driverCode];
          return acc;
        },
        {
          matrixDaysUpdate: {},
          newMatrixDays: {},
        }
      );

      const { data: deleteResponse } = await api.put(
        REMOVE_DRIVER_PROFILE(driverCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          checksum: driverProfileLocks[driverCode].checksum,
          matrixDays: matrixDaysUpdate,
        }
      );

      if (!deleteResponse.success)
        throw new Error("Failed to remove driver profile");

      const newDriverProfileLocks = { ...driverProfileLocks };
      delete newDriverProfileLocks[driverCode];
      deleteDriverManagementDriverProfileChecksum({ driverCode });

      const newMatrixDaysWithUpdatedLocks = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          const newRouteDayLocks = Object.keys(
            deleteResponse.matrixDaysLocks[dayOfWeek].routeDayLocks
          ).reduce(
            (acc, routeCode) => {
              const {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
              } =
                deleteResponse.matrixDaysLocks[dayOfWeek].routeDayLocks[
                  routeCode
                ];
              acc[routeCode] = {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
                lockedByYou: true,
                lockedInDifferentBrowser: false,
              };
              saveDriverManagementRouteDayChecksum({
                dayOfWeek,
                depotCode: selectedDepot,
                routeCode,
                checksum,
              });
              return acc;
            },
            { ...newMatrixDays[dayOfWeek].routeDayLocks }
          );

          const newDriverDayLocks = Object.keys(
            deleteResponse.matrixDaysLocks[dayOfWeek].driverDayLocks
          ).reduce(
            (acc, driverCode) => {
              const {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
              } =
                deleteResponse.matrixDaysLocks[dayOfWeek].driverDayLocks[
                  driverCode
                ];
              acc[driverCode] = {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
                lockedByYou: true,
                lockedInDifferentBrowser: false,
              };
              saveDriverManagementDriverDayChecksum({
                dayOfWeek,
                driverCode,
                checksum,
              });
              return acc;
            },
            { ...newMatrixDays[dayOfWeek].driverDayLocks }
          );

          deleteDriverManagementDriverDayChecksum({ dayOfWeek, driverCode });

          acc[dayOfWeek] = {
            ...newMatrixDays[dayOfWeek],
            routeDayLocks: newRouteDayLocks,
            driverDayLocks: newDriverDayLocks,
          };
          return acc;
        },
        { ...newMatrixDays }
      );

      syncToAsync(() => dispatch(unlockDepotWeek()));

      return {
        driverProfiles: newDriverProfiles,
        driverProfileLocks: newDriverProfileLocks,
        matrixDays: newMatrixDaysWithUpdatedLocks,
      };
    } catch (error) {
      await dispatch(unlockDepotWeek());
      throw error;
    } finally {
      dispatch(resetDriver());
    }
  }
);

export const lockDriverProfile = createAsyncThunk(
  "driverManagement/lockDriverProfile",
  async ({ driverCode }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverWeekLocks = driverWeekLocksSelector(driverCode)(state);

    try {
      const { data: lockResponse } = await api.put(
        LOCK_DRIVER_PROFILE(driverCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          checksum: driverProfileLock.checksum,
          weekChecksums: Object.entries(driverWeekLocks).reduce(
            (acc, [dayOfWeek, { checksum }]) => {
              acc[dayOfWeek] = checksum;
              return acc;
            },
            {}
          ),
        }
      );
      if (!lockResponse.success)
        throw new Error(DRIVER_PROFILE_FAILED_TO_LOCK(driverProfile));
      dispatch(
        addNotification({
          message: DRIVER_PROFILE_SUCESSFULLY_LOCKED(driverProfile),
          options: { variant: "success" },
        })
      );
      saveDriverManagementDriverProfileChecksum({
        driverCode,
        checksum: driverProfileLock.checksum,
      });
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        saveDriverManagementDriverDayChecksum({
          dayOfWeek,
          driverCode,
          checksum: driverWeekLocks[dayOfWeek].checksum,
        })
      );

      return {
        driverCode,
        lockedByUserCode: lockResponse.lockedByUserCode,
        lockedByUserName: lockResponse.lockedByUserName,
        lockedDatetime: lockResponse.lockedDatetime,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [
          DRIVER_PROFILE_CHANGED,
          DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY,
          DRIVER_DAY_CHANGED,
        ].includes(error.statusCode)
      )
        dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const unlockDriverProfile = createAsyncThunk(
  "driverManagement/unlockDriverProfile",
  async ({ driverCode }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverWeekLocks = driverWeekLocksSelector(driverCode)(state);

    try {
      const { data: unlockResponse } = await api.put(
        UNLOCK_DRIVER_PROFILE(driverCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          checksum: driverProfileLock.checksum,
          weekChecksums: Object.entries(driverWeekLocks).reduce(
            (acc, [dayOfWeek, { checksum }]) => {
              acc[dayOfWeek] = checksum;
              return acc;
            },
            {}
          ),
        }
      );
      if (!unlockResponse.success)
        throw new Error(DRIVER_PROFILE_FAILED_TO_UNLOCK(driverProfile));
      dispatch(
        addNotification({
          message: DRIVER_PROFILE_SUCESSFULLY_UNLOCKED(driverProfile),
          options: { variant: "success" },
        })
      );
      deleteDriverManagementDriverProfileChecksum({ driverCode });
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        deleteDriverManagementDriverDayChecksum({
          dayOfWeek,
          driverCode,
        })
      );
      return { driverCode };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [
          DRIVER_PROFILE_CHANGED,
          DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY,
          DRIVER_DAY_CHANGED,
        ].includes(error.statusCode)
      )
        dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const lockDriverDay = createAsyncThunk(
  "driverManagement/lockDriverDay",
  async (
    { driverCode, dayOfWeek },
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverDayLock = driverDayLockSelector(dayOfWeek, driverCode)(state);

    try {
      const { data: lockResponse } = await api.put(
        LOCK_DRIVER_DAY(driverCode, dayOfWeek),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          driverProfileChecksum: driverProfileLock.checksum,
          driverDayChecksum: driverDayLock.checksum,
        }
      );
      if (!lockResponse.success)
        throw new Error(DRIVER_DAY_FAILED_TO_LOCK(driverProfile, dayOfWeek));
      dispatch(
        addNotification({
          message: DRIVER_DAY_SUCESSFULLY_LOCKED(driverProfile, dayOfWeek),
          options: { variant: "success" },
        })
      );
      saveDriverManagementDriverDayChecksum({
        dayOfWeek,
        driverCode,
        checksum: driverDayLock.checksum,
      });
      return {
        dayOfWeek,
        driverCode,
        lockedByUserCode: lockResponse.lockedByUserCode,
        lockedByUserName: lockResponse.lockedByUserName,
        lockedDatetime: lockResponse.lockedDatetime,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [DRIVER_PROFILE_CHANGED, DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY].includes(
          error.statusCode
        )
      )
        dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_LOCKED === error.statusCode)
        (driverDayLock.lockedByYou ||
          driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverDayLock.lockedByYou ||
          !driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const unlockDriverDay = createAsyncThunk(
  "driverManagement/unlockDriverDay",
  async (
    { dayOfWeek, driverCode },
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverDayLock = driverDayLockSelector(dayOfWeek, driverCode)(state);

    try {
      const { data: unlockResponse } = await api.put(
        UNLOCK_DRIVER_DAY(driverCode, dayOfWeek),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          driverProfileChecksum: driverProfileLock.checksum,
          driverDayChecksum: driverDayLock.checksum,
        }
      );
      if (!unlockResponse.success)
        throw new Error(DRIVER_DAY_FAILED_TO_UNLOCK(driverProfile, dayOfWeek));
      dispatch(
        addNotification({
          message: DRIVER_DAY_SUCESSFULLY_UNLOCKED(driverProfile, dayOfWeek),
          options: { variant: "success" },
        })
      );
      deleteDriverManagementDriverDayChecksum({ dayOfWeek, driverCode });
      return { dayOfWeek, driverCode };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [DRIVER_PROFILE_CHANGED, DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY].includes(
          error.statusCode
        )
      )
        dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_LOCKED === error.statusCode)
        (driverDayLock.lockedByYou ||
          driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired("profile"));
      else if (DRIVER_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverDayLock.lockedByYou ||
          !driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const lockRouteDay = createAsyncThunk(
  "driverManagement/lockRouteDay",
  async (
    { depotCode, dayOfWeek, routeCode },
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState();
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const routeDayLock = routeDayLockSelector(dayOfWeek, routeCode)(state);

    try {
      const { data: lockResponse } = await api.put(
        LOCK_ROUTE_DAY(depotCode, dayOfWeek, routeCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          checksum: routeDayLock.checksum,
        }
      );
      if (!lockResponse.success)
        throw new Error(ROUTE_DAY_FAILED_TO_LOCK(routeCode, dayOfWeek));
      dispatch(
        addNotification({
          message: ROUTE_DAY_SUCESSFULLY_LOCKED(routeCode, dayOfWeek),
          options: { variant: "success" },
        })
      );
      saveDriverManagementRouteDayChecksum({
        dayOfWeek,
        depotCode,
        routeCode,
        checksum: routeDayLock.checksum,
      });
      return {
        dayOfWeek,
        routeCode,
        lockedByUserCode: lockResponse.lockedByUserCode,
        lockedByUserName: lockResponse.lockedByUserName,
        lockedDatetime: lockResponse.lockedDatetime,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (ROUTE_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (ROUTE_DAY_LOCKED === error.statusCode)
        (routeDayLock.lockedByYou ||
          routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (ROUTE_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!routeDayLock.lockedByYou ||
          !routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const unlockRouteDay = createAsyncThunk(
  "driverManagement/unlockRouteDay",
  async (
    { depotCode, dayOfWeek, routeCode },
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState();
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const routeDayLock = routeDayLockSelector(dayOfWeek, routeCode)(state);

    try {
      const { data: unlockResponse } = await api.put(
        UNLOCK_ROUTE_DAY(depotCode, dayOfWeek, routeCode),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          checksum: routeDayLock.checksum,
        }
      );
      if (!unlockResponse.success)
        throw new Error(ROUTE_DAY_FAILED_TO_UNLOCK(routeCode, dayOfWeek));
      dispatch(
        addNotification({
          message: ROUTE_DAY_SUCESSFULLY_UNLOCKED(routeCode, dayOfWeek),
          options: { variant: "success" },
        })
      );
      deleteDriverManagementRouteDayChecksum({
        dayOfWeek,
        depotCode,
        routeCode,
      });
      return { dayOfWeek, routeCode };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (ROUTE_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (ROUTE_DAY_LOCKED === error.statusCode)
        (routeDayLock.lockedByYou ||
          routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));
      else if (ROUTE_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!routeDayLock.lockedByYou ||
          !routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(dayOfWeek));

      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const editDriver = createAsyncThunk(
  "driverManagement/editDriver",
  async ({ driverCode }, { dispatch, getState }) => {
    const state = getState();
    const driverProfile = driverProfileSelector(driverCode)(state);
    const matrixDays = matrixDaysSelector(state);

    dispatch(setSelectedDriver(null));
    return {
      driverEdit: {
        driverCode,
        driverProfileEdit: { ...driverProfile },
        driverDayEdits: getDriverDayEdits({
          driverCode,
          matrixDays,
        }),
      },
    };
  }
);

export const resetDriver = createAction("driverManagement/resetDriver");

// Exported factory function has to be used to make lockRouteDay/lockDriverDay mock-able when testing
export const updateDriverDayEditFactory = ({
  lockRouteDay,
  lockDriverDay,
  unlockRouteDay,
  unlockDriverDay,
  routeDayLockSelector,
  driverDayLockSelector,
}) =>
  createAsyncThunk(
    "driverManagement/updateDriverDayEdit",
    async (
      { dayOfWeek, name, value, disableLocking = false, keepLoading = false },
      { dispatch, getState }
    ) => {
      const state = getState();
      const depotCode = getSelectedDepot(state);
      const driversImpacted = driversImpactedSelector(state);
      const driverEdit = driverEditSelector(state);
      const drivers = matrixDayDriversSelector(dayOfWeek)(state);
      const routes = matrixDayRoutesSelector(dayOfWeek)(state);
      const driverDayEdit = driverDayEditSelector(dayOfWeek)(state);
      const driverDayEditCopy = driverDayEditCopySelector(dayOfWeek)(state);

      const newDriverDayEdit = await updateDriverDay({
        disableLocking,
        update: { name, value },
        drivers,
        routes,
        driverEdit: driverDayEdit,
        driverEditCopy: driverDayEditCopy,

        getRouteDayLock: (routeCode) =>
          routeDayLockSelector(dayOfWeek, routeCode)(getState()),
        lockRouteDayAction: (routeCode) =>
          dispatch(lockRouteDay({ depotCode, dayOfWeek, routeCode })),
        unlockRouteDayAction: (routeCode) =>
          dispatch(unlockRouteDay({ depotCode, dayOfWeek, routeCode })),

        getDriverDayLock: (driverCode) =>
          driverDayLockSelector(dayOfWeek, driverCode)(getState()),
        lockDriverDayAction: (driverCode) =>
          dispatch(lockDriverDay({ dayOfWeek, driverCode })),
        unlockDriverDayAction: (driverCode) =>
          dispatch(unlockDriverDay({ dayOfWeek, driverCode })),

        addNotificationAction: (message) =>
          dispatch(
            addNotification({
              message,
              options: { variant: "error" },
            })
          ),

        onFinally: async () => {
          const afterLockingState = getState();
          const userSessionExpired =
            userSessionExpiredSelector(afterLockingState);
          const locksRefreshRequired =
            locksRefreshRequiredSelector(afterLockingState);
          if (userSessionExpired) {
            dispatch(resetDriver());
            dispatch(setDriverManagementExpiredModalOpen(true));
            return;
          }

          if (locksRefreshRequired["profile"])
            await dispatch(refreshDepotMatrixWeekLocks());
          // This has to be done in serial, do not change to parallel
          else
            for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
              if (locksRefreshRequired[dayOfWeek])
                await dispatch(refreshDepotMatrixDayLocks({ dayOfWeek }));
        },
      });

      const { driversImpacted: driversDayImpacted } =
        getUpdateImpactOnMatrixDay({
          dayOfWeek,
          driverEdit: newDriverDayEdit,
          driverEditCopy: driverDayEditCopy,
          drivers,
          routes,
        });

      let newDriversImpacted = driversImpacted;
      if (!isEqual(driversImpacted[dayOfWeek], driversDayImpacted))
        newDriversImpacted = {
          ...driversImpacted,
          [dayOfWeek]: driversDayImpacted,
        };

      return {
        driversImpacted: newDriversImpacted,
        driverEdit: {
          ...driverEdit,
          driverDayEdits: {
            ...driverEdit.driverDayEdits,
            [dayOfWeek]: newDriverDayEdit,
          },
        },
        keepLoading,
      };
    }
  );
export const updateDriverDayEdit = updateDriverDayEditFactory({
  lockDriverDay,
  lockRouteDay,
  unlockDriverDay,
  unlockRouteDay,
  routeDayLockSelector,
  driverDayLockSelector,
});

export const copyDriverDayEdit = createAsyncThunk(
  "driverManagement/copyDriverDayEdit",
  async ({ sourceDayOfWeek, targetDayOfWeek }, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const driversImpacted = driversImpactedSelector(state);
    const driverEdit = driverEditSelector(state);
    const sourceDriverDayEdit = driverDayEditSelector(sourceDayOfWeek)(state);
    const drivers = matrixDayDriversSelector(targetDayOfWeek)(state);
    const routes = matrixDayRoutesSelector(targetDayOfWeek)(state);
    const driverDayEdit = driverDayEditSelector(targetDayOfWeek)(state);
    const driverDayEditCopy = driverDayEditCopySelector(targetDayOfWeek)(state);

    const updateFields = [
      "driverCode",
      "plannedOdf",
      "routeCode",
      "deliveryStopTime",
      "collectionStopTime",
      "dutyStartTime",
      "dutyEndTime",
      "plannedDepartTime",
      "plannedReturnTime",
      "sectionCode",
      "startAtSatellite",
      "endAtSatellite",
    ];

    let newDriverDayEdit = { ...driverDayEdit };
    for (const field of updateFields) {
      if (sourceDriverDayEdit[field] === newDriverDayEdit[field]) continue;
      newDriverDayEdit = await updateDriverDay({
        update: {
          name: field,
          value: sourceDriverDayEdit[field],
        },
        drivers,
        routes,
        driverEdit: newDriverDayEdit,
        driverEditCopy: driverDayEditCopy,

        getRouteDayLock: (routeCode) =>
          routeDayLockSelector(targetDayOfWeek, routeCode)(getState()),
        lockRouteDayAction: (routeCode) =>
          dispatch(
            lockRouteDay({ depotCode, dayOfWeek: targetDayOfWeek, routeCode })
          ),
        unlockRouteDayAction: (routeCode) =>
          dispatch(
            unlockRouteDay({ depotCode, dayOfWeek: targetDayOfWeek, routeCode })
          ),

        getDriverDayLock: (driverCode) =>
          driverDayLockSelector(targetDayOfWeek, driverCode)(getState()),
        lockDriverDayAction: (driverCode) =>
          dispatch(lockDriverDay({ dayOfWeek: targetDayOfWeek, driverCode })),
        unlockDriverDayAction: (driverCode) =>
          dispatch(unlockDriverDay({ dayOfWeek: targetDayOfWeek, driverCode })),

        addNotificationAction: (message) =>
          dispatch(
            addNotification({
              message,
              options: { variant: "error" },
            })
          ),

        onFinally: async () => {
          const afterLockingState = getState();
          const userSessionExpired =
            userSessionExpiredSelector(afterLockingState);
          const locksRefreshRequired =
            locksRefreshRequiredSelector(afterLockingState);
          if (userSessionExpired) {
            dispatch(resetDriver());
            dispatch(setDriverManagementExpiredModalOpen(true));
            return;
          }

          if (locksRefreshRequired["profile"])
            await dispatch(refreshDepotMatrixWeekLocks());
          // This has to be done in serial, do not change to parallel
          else
            for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
              if (locksRefreshRequired[dayOfWeek])
                await dispatch(refreshDepotMatrixDayLocks({ dayOfWeek }));
        },
      });
    }

    const { driversImpacted: driversDayImpacted } = getUpdateImpactOnMatrixDay({
      dayOfWeek: targetDayOfWeek,
      driverEdit: newDriverDayEdit,
      driverEditCopy: driverDayEditCopy,
      drivers,
      routes,
    });

    let newDriversImpacted = driversImpacted;
    if (!isEqual(driversImpacted[targetDayOfWeek], driversDayImpacted))
      newDriversImpacted = {
        ...driversImpacted,
        [targetDayOfWeek]: driversDayImpacted,
      };

    return {
      driversImpacted: newDriversImpacted,
      driverEdit: {
        ...driverEdit,
        driverDayEdits: {
          ...driverEdit.driverDayEdits,
          [targetDayOfWeek]: newDriverDayEdit,
        },
      },
    };
  }
);

export const updateDriverProfileEdit = createAsyncThunk(
  "driverManagement/updateDriverProfileEdit",
  async ({ updates = [], keepLoading = false }, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const driverEdit = driverEditSelector(state);
    const { driverCode, driverProfileEdit, driverDayEdits } = driverEdit;

    const onLockDriverProfile = async () => {
      const { lockedByYou, lockedInDifferentBrowser, changed } =
        driverProfileLockSelector(driverCode)(getState()) || {};
      if (changed) {
        dispatch(
          addNotification({
            message: `Driver ${driverCode.split("*")[1]} ${
              driverProfileEdit.driverName
            } has change.`,
            options: { variant: "error" },
          })
        );
        throw new Error(
          `Driver ${driverCode.split("*")[1]} ${
            driverProfileEdit.driverName
          } has change.`
        );
      } else if (lockedByYou && !lockedInDifferentBrowser) return;
      const { error } = await dispatch(lockDriverProfile({ driverCode }));
      if (error) throw error;
    };

    const onLockRouteDay = async (dayOfWeek, routeCode) => {
      const { lockedByYou, lockedInDifferentBrowser, changed } =
        routeDayLockSelector(dayOfWeek, routeCode)(state) || {};
      if (changed) {
        dispatch(
          addNotification({
            message: `Route ${routeCode} has change.`,
            options: { variant: "error" },
          })
        );
        throw new Error(`Route ${routeCode} has change.`);
      } else if (lockedByYou && !lockedInDifferentBrowser) return;
      const { error } = await dispatch(
        lockRouteDay({ depotCode: selectedDepot, dayOfWeek, routeCode })
      );
      if (error) throw error;
    };

    const onLockDriverDay = async (dayOfWeek, driverCode) => {
      const { lockedByYou, lockedInDifferentBrowser, changed } =
        driverDayLockSelector(dayOfWeek, driverCode)(state) || {};
      if (changed) {
        dispatch(
          addNotification({
            message: `Driver ${driverCode} has change.`,
            options: { variant: "error" },
          })
        );
        throw new Error(`Driver ${driverCode} has change.`);
      } else if (lockedByYou && !lockedInDifferentBrowser) return;
      const { error } = await dispatch(
        lockDriverDay({ driverCode, dayOfWeek })
      );
      if (error) throw error;
    };

    try {
      await onLockDriverProfile();
      await Promise.all([
        ...[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
          const { routeCode } = driverDayEdits[dayOfWeek];
          return (
            isNonRedirectionRouteCode(routeCode) &&
            onLockRouteDay(dayOfWeek, routeCode)
          );
        }),
        ...[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
          const { driverCode: driverDayCode, plannedOdf } =
            driverDayEdits[dayOfWeek];
          if (driverDayCode !== driverCode)
            return onLockDriverDay(dayOfWeek, driverDayCode);
          else if (!!plannedOdf && plannedOdf !== driverCode)
            return onLockDriverDay(dayOfWeek, plannedOdf);
          else return null;
        }),
      ]);
    } finally {
      const afterLockingState = getState();
      const userSessionExpired = userSessionExpiredSelector(afterLockingState);
      const locksRefreshRequired =
        locksRefreshRequiredSelector(afterLockingState);
      if (userSessionExpired) {
        dispatch(resetDriver());
        dispatch(setDriverManagementExpiredModalOpen(true));
        return;
      }

      if (locksRefreshRequired["profile"])
        await dispatch(refreshDepotMatrixWeekLocks());
      // This has to be done in serial, do not change to parallel
      else
        for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6])
          if (locksRefreshRequired[dayOfWeek])
            await dispatch(refreshDepotMatrixDayLocks({ dayOfWeek }));
    }

    const driverTypeUpdate = updates.find(({ name }) => name === "driverType");
    // Unallocates route no longer suitable for new driver type
    if (driverTypeUpdate) {
      for (const dayOfWeek of [0, 1, 2, 3, 4, 5, 6]) {
        const drivers = matrixDayDriversSelector(dayOfWeek)(state);
        const routes = matrixDayRoutesSelector(dayOfWeek)(state);
        const { driverName, routeCode } = drivers[driverCode];
        const route = routes.find((route) => route.routeCode === routeCode);
        if (
          route?.routeType &&
          // NON-ROBOT DRIVER ON ROBOT ROUTE
          ((ROBOTS_PLAN_TYPE === route.routeType &&
            ROBOT_DRIVER_TYPE !== driverTypeUpdate.value) ||
            // ROBOT DRIVER ON NON-ROBOT ROUTE
            (ROBOTS_PLAN_TYPE !== route.routeType &&
              ROBOT_DRIVER_TYPE === driverTypeUpdate.value) ||
            // NON-CHOICE DRIVER ON CHOICE ROUTE
            (CHOICE_PLAN_TYPE === route.routeType &&
              CHOICE_DRIVER_TYPE !== driverTypeUpdate.value) ||
            // CHOICE DRIVER ON NON-CHOICE ROUTE
            (CHOICE_PLAN_TYPE !== route.routeType &&
              CHOICE_DRIVER_TYPE === driverTypeUpdate.value))
        ) {
          const { error } = await dispatch(
            updateDriverDayEdit({
              dayOfWeek,
              name: "routeCode",
              value: null,
              keepLoading: true,
            })
          );
          if (error) throw error;
          else
            dispatch(
              addNotification({
                message: ROUTE_DAY_NOT_SUITABLE_WARNING({
                  driverCode,
                  driverName,
                  routeCode,
                  dayOfWeek,
                }),
                options: { variant: "warning" },
              })
            );
        }
      }
    }

    let newDriverDayEdits = driverDayEditsSelector(getState());
    const daysInheritUpdatesNames = [
      "driverType",
      "vehicleType",
      "startPostcode",
      "startLatitude",
      "startLongitude",
      "endPostcode",
      "endLatitude",
      "endLongitude",
    ];

    const newDriverProfileEdit = updateDriverProfile({
      updates,
      driverProfileEdit,
    });

    daysInheritUpdatesNames.forEach((daysInheritUpdateName) => {
      const daysInheritUpdate = updates.find(
        ({ name }) => name === daysInheritUpdateName
      );
      if (!daysInheritUpdate) return;
      newDriverDayEdits = [0, 1, 2, 3, 4, 5, 6].reduce((acc, dayOfWeek) => {
        acc[dayOfWeek] = {
          ...newDriverDayEdits[dayOfWeek],
        };
        acc[dayOfWeek][daysInheritUpdate.name] = daysInheritUpdate.value;
        return acc;
      }, {});
    });

    return { newDriverProfileEdit, newDriverDayEdits, keepLoading };
  }
);

export const unlockDriverProfiles = createAsyncThunk(
  "depotManagement/unlockDriverProfiles",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const drivers = driverProfilesSelector(state);
    const driverProfileLocks = driverProfileLocksSelector(state);

    const changedDrivers = new Set();
    await Promise.allSettled(
      Object.entries(driverProfileLocks)
        .filter(
          ([driverCode, { lockedByYou, lockedInDifferentBrowser }]) =>
            lockedByYou && !lockedInDifferentBrowser
        )
        .map(async ([driverCode]) => {
          const { error, meta } = await dispatch(
            unlockDriverProfile({ driverCode })
          );
          if (
            error &&
            [DRIVER_PROFILE_CHANGED, DRIVER_DAY_CHANGED].includes(
              meta.statusCode
            )
          )
            changedDrivers.add(drivers[driverCode].driverName);
          if (error) throw error;
        })
    );

    if (!changedDrivers.size) return;
    await dispatch(fetchDepotMatrixWeek({ depotCode: selectedDepot }));
  }
);

export const unlockMatrixDay = createAsyncThunk(
  "depotManagement/unlockMatrixDay",
  async ({ dayOfWeek }, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const drivers = driverProfilesSelector(state);
    const routeDayLocks = routeDayLocksSelector(dayOfWeek)(state);
    const driverDayLocks = driverDayLocksSelector(dayOfWeek)(state);

    const changedRoutes = new Set();
    const changedDrivers = new Set();
    await Promise.allSettled([
      ...Object.entries(routeDayLocks)
        .filter(
          ([routeCode, { lockedByYou, lockedInDifferentBrowser }]) =>
            lockedByYou && !lockedInDifferentBrowser
        )
        .map(async ([routeCode]) => {
          const { error, meta } = await dispatch(
            unlockRouteDay({ depotCode: selectedDepot, dayOfWeek, routeCode })
          );
          if (error && meta.statusCode === ROUTE_DAY_CHANGED)
            changedRoutes.add(routeCode);
          if (error) throw error;
        }),
      ...Object.entries(driverDayLocks)
        .filter(
          ([driverCode, { lockedByYou, lockedInDifferentBrowser }]) =>
            lockedByYou && !lockedInDifferentBrowser
        )
        .map(async ([driverCode]) => {
          const { error, meta } = await dispatch(
            unlockDriverDay({ dayOfWeek, driverCode })
          );
          if (
            error &&
            [DRIVER_PROFILE_CHANGED, DRIVER_DAY_CHANGED].includes(
              meta.statusCode
            )
          )
            changedDrivers.add(drivers[driverCode].driverName);
          if (error) throw error;
        }),
    ]);

    if (!changedRoutes.size && !changedDrivers.size) return;
    dispatch(fetchDepotMatrixWeek({ depotCode: selectedDepot }));
  }
);

export const unlockDepotWeek = createAsyncThunk(
  "driverManagement/unlockDepotWeek",
  async (params, { dispatch }) => {
    await dispatch(unlockDriverProfiles());
    for (const dayOfWeek of [1, 2, 3, 4, 5, 6, 0])
      await dispatch(unlockMatrixDay({ dayOfWeek }));
    dispatch(setSelectedDriver(null));
  }
);

export const forceUnlockMatrixWeek = createAsyncThunk(
  "driverManagement/forceUnlockMatrixWeek",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);

    try {
      const { data } = await api.put(
        FORCE_UNLOCK_MATRIX_WEEK(selectedDepot),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
        }
      );

      if (!data.success) throw new Error(DRIVER_FORCE_UNLOCK_FAILED);
      deleteDriverManagementDepotDriverProfileChecksums({
        depotCode: selectedDepot,
      });
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        deleteDriverManagementDepotDriverDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        })
      );
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        deleteDriverManagementDepotRouteDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        })
      );
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_MATRIX_WEEK,
          options: { variant: "success" },
        })
      );
      dispatch(setSelectedDriver(null));
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }

    await dispatch(fetchDepotMatrixWeek({ depotCode: selectedDepot }));
  }
);

export const forceUnlockDriverWeek = createAsyncThunk(
  "matrixProfile/forceUnlockDriverWeek",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);

    try {
      const { data } = await api.put(
        FORCE_UNLOCK_DEPOT_DRIVERS(selectedDepot),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
        }
      );

      if (!data.success) throw new Error(DRIVER_FORCE_UNLOCK_FAILED);
      deleteDriverManagementDepotDriverProfileChecksums({
        depotCode: selectedDepot,
      });
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        deleteDriverManagementDepotDriverDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        })
      );
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_DEPOT_DRIVERS,
          options: { variant: "success" },
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }

    await dispatch(fetchDepotMatrixWeek({ depotCode: selectedDepot }));
  }
);

export const setIsDriverEditProfileValid = createAction(
  "driverManagement/setIsDriverEditProfileValid",
  (isValid) => ({ payload: isValid })
);

export const setIsDriverEditWorkScheduleValid = createAction(
  "driverManagement/setIsDriverEditWorkScheduleValid",
  (isValid) => ({ payload: isValid })
);

export const saveDriverProfile = createAsyncThunk(
  "driverManagement/saveDriverProfile",
  async (params, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverEdit = driverEditSelector(state);
    const { driverCode, driverProfileEdit } = driverEdit;
    const driverProfileLocks = driverProfileLocksSelector(state);
    const driverProfileLock = driverProfileLocks[driverCode];
    const driverImage = driverImageSelector(driverCode)(state);

    try {
      const { newMatrixDays, matrixDaysUpdate } = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          const { drivers, routes, driverDayLocks, routeDayLocks } =
            matrixDaySelector(dayOfWeek)(state);
          const driverEdit = driverDayEditSelector(dayOfWeek)(state);
          const driverEditCopy = driverDayEditCopySelector(dayOfWeek)(state);

          const updatedProfileDrivers = {
            ...drivers,
            [driverCode]: {
              ...drivers[driverCode],
              driverType: driverProfileEdit.driverType,
              vehicleType: driverProfileEdit.vehicleType,
            },
          };

          if (driverEdit === driverEditCopy) {
            acc.newMatrixDays[dayOfWeek] = {
              routes,
              drivers: updatedProfileDrivers,
              driverDayLocks,
              routeDayLocks,
            };
            acc.matrixDaysUpdate[dayOfWeek] = {
              hasChanges: false,
              routeDays: generateRouteDaysConfig({
                routes: driverEdit.routeCode ? [driverEdit] : [],
                routeDayLocks,
              }),
              drivers: generateDriverProfilesConfig({
                drivers: driverEdit.plannedOdf
                  ? [driverEdit, { driverCode: driverEdit.plannedOdf }]
                  : [driverEdit],
                driverProfileLocks,
              }),
              driverDays: generateDriverDaysConfig({
                drivers: driverEdit.plannedOdf
                  ? [driverEdit, { driverCode: driverEdit.plannedOdf }]
                  : [driverEdit],
                driverDayLocks,
              }),
            };
          } else {
            const {
              impactedRoutes,
              impactedDrivers,
              routes: newRoutes,
              drivers: newDrivers,
            } = applyChangeToMatrixDay({
              dayOfWeek,
              driverEdit,
              driverEditCopy,
              drivers: updatedProfileDrivers,
              routes,
            });

            acc.newMatrixDays[dayOfWeek] = {
              routes: newRoutes,
              drivers: newDrivers,
              driverDayLocks,
              routeDayLocks,
            };
            acc.matrixDaysUpdate[dayOfWeek] = {
              hasChanges: true,
              routeDays: generateRouteDaysConfig({
                routes: impactedRoutes,
                routeDayLocks,
              }),
              drivers: generateDriverProfilesConfig({
                drivers: impactedDrivers,
                driverProfileLocks,
              }),
              driverDays: generateDriverDaysConfig({
                drivers: impactedDrivers,
                driverDayLocks,
              }),
            };
          }
          return acc;
        },
        {
          matrixDaysUpdate: {},
          newMatrixDays: {},
        }
      );
      const requestBody = {
        templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
        sessionChecksum,
        checksum: driverProfileLock.checksum,
        driverProfile: driverProfileEdit,
        matrixDays: matrixDaysUpdate,
      };

      if (driverEdit.imageChanged) requestBody.driverImage = driverImage;

      const { data: saveResponse } = await api.put(
        UPDATE_DRIVER_PROFILE(driverCode),
        null,
        requestBody
      );

      if (!saveResponse.success)
        throw new Error("Failed to save driver profile");

      const newDriverProfileLocks = {
        ...driverProfileLocks,
        [driverCode]: {
          ...saveResponse.driverProfileLock,
          lockedByYou: true,
          lockedInDifferentBrowser: false,
        },
      };
      saveDriverManagementDriverProfileChecksum({
        driverCode,
        checksum: newDriverProfileLocks[driverCode].checksum,
      });

      const newMatrixDaysWithUpdatedLocks = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          const newRouteDayLocks = Object.keys(
            saveResponse.matrixDaysLocks[dayOfWeek].routeDayLocks
          ).reduce(
            (acc, routeCode) => {
              const {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
              } =
                saveResponse.matrixDaysLocks[dayOfWeek].routeDayLocks[
                  routeCode
                ];
              acc[routeCode] = {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
                lockedByYou: true,
                lockedInDifferentBrowser: false,
              };
              saveDriverManagementRouteDayChecksum({
                dayOfWeek,
                depotCode: selectedDepot,
                routeCode,
                checksum,
              });
              return acc;
            },
            { ...newMatrixDays[dayOfWeek].routeDayLocks }
          );

          const newDriverDayLocks = Object.keys(
            saveResponse.matrixDaysLocks[dayOfWeek].driverDayLocks
          ).reduce(
            (acc, driverCode) => {
              const {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
              } =
                saveResponse.matrixDaysLocks[dayOfWeek].driverDayLocks[
                  driverCode
                ];
              acc[driverCode] = {
                checksum,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
                lockedByYou: true,
                lockedInDifferentBrowser: false,
              };
              saveDriverManagementDriverDayChecksum({
                dayOfWeek,
                driverCode,
                checksum,
              });
              return acc;
            },
            { ...newMatrixDays[dayOfWeek].driverDayLocks }
          );

          acc[dayOfWeek] = {
            ...newMatrixDays[dayOfWeek],
            routeDayLocks: newRouteDayLocks,
            driverDayLocks: newDriverDayLocks,
          };
          return acc;
        },
        { ...newMatrixDays }
      );

      dispatch(
        addNotification({
          message: `Successfully saved driver profile changes.`,
          options: { variant: "success" },
        })
      );

      syncToAsync(() => dispatch(unlockDepotWeek()));
      return {
        newDriverProfile: driverProfileEdit,
        newDriverProfileLocks,
        newMatrixDays: newMatrixDaysWithUpdatedLocks,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const saveMatrixWeek = createAsyncThunk(
  "driverManagement/saveMatrixWeek",
  async (params, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const sessionChecksum = driverManagementSessionChecksumSelector(state);
    const driverProfileLocks = driverProfileLocksSelector(state);
    const matrixDays = matrixDaysSelector(state);

    try {
      const { newMatrixDays, matrixDaysUpdate } = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          const { drivers, routes, driverDayLocks, routeDayLocks } =
            matrixDaySelector(dayOfWeek)(state);
          const driverEdit = driverDayEditSelector(dayOfWeek)(state);
          const driverEditCopy = driverDayEditCopySelector(dayOfWeek)(state);

          if (driverEdit !== driverEditCopy) {
            const {
              impactedRoutes,
              impactedDrivers,
              routes: newRoutes,
              drivers: newDrivers,
            } = applyChangeToMatrixDay({
              dayOfWeek,
              driverEdit,
              driverEditCopy,
              drivers,
              routes,
            });

            acc.newMatrixDays[dayOfWeek] = {
              routes: newRoutes,
              drivers: newDrivers,
              driverDayLocks,
              routeDayLocks,
            };
            acc.matrixDaysUpdate[dayOfWeek] = {
              hasChanges: true,
              routeDays: generateRouteDaysConfig({
                routes: impactedRoutes,
                routeDayLocks,
              }),
              drivers: generateDriverProfilesConfig({
                drivers: impactedDrivers,
                driverProfileLocks,
              }),
              driverDays: generateDriverDaysConfig({
                drivers: impactedDrivers,
                driverDayLocks,
              }),
            };
          }
          return acc;
        },
        {
          matrixDaysUpdate: {},
          newMatrixDays: {},
        }
      );
      const { data: saveResponse } = await api.put(
        UPDATE_MATRIX_WEEK(selectedDepot),
        null,
        {
          templateId: "DRIVER_MANAGEMENT", // TODO rename to sessionId ???
          sessionChecksum,
          matrixDays: matrixDaysUpdate,
        }
      );

      if (!saveResponse.success) throw new Error("Failed to save matrix week");

      const newMatrixDaysWithUpdatedLocks = [0, 1, 2, 3, 4, 5, 6].reduce(
        (acc, dayOfWeek) => {
          if (saveResponse.matrixDaysLocks[dayOfWeek]) {
            const newRouteDayLocks = Object.keys(
              saveResponse.matrixDaysLocks[dayOfWeek].routeDayLocks
            ).reduce(
              (acc, routeCode) => {
                const {
                  checksum,
                  lockedByUserCode,
                  lockedByUserName,
                  lockedDatetime,
                } =
                  saveResponse.matrixDaysLocks[dayOfWeek].routeDayLocks[
                    routeCode
                  ];
                acc[routeCode] = {
                  checksum,
                  lockedByUserCode,
                  lockedByUserName,
                  lockedDatetime,
                  lockedByYou: true,
                  lockedInDifferentBrowser: false,
                };
                saveDriverManagementRouteDayChecksum({
                  dayOfWeek,
                  depotCode: selectedDepot,
                  routeCode,
                  checksum,
                });
                return acc;
              },
              { ...newMatrixDays[dayOfWeek].routeDayLocks }
            );

            const newDriverDayLocks = Object.keys(
              saveResponse.matrixDaysLocks[dayOfWeek].driverDayLocks
            ).reduce(
              (acc, driverCode) => {
                const {
                  checksum,
                  lockedByUserCode,
                  lockedByUserName,
                  lockedDatetime,
                } =
                  saveResponse.matrixDaysLocks[dayOfWeek].driverDayLocks[
                    driverCode
                  ];
                acc[driverCode] = {
                  checksum,
                  lockedByUserCode,
                  lockedByUserName,
                  lockedDatetime,
                  lockedByYou: true,
                  lockedInDifferentBrowser: false,
                };
                saveDriverManagementDriverDayChecksum({
                  dayOfWeek,
                  driverCode,
                  checksum,
                });
                return acc;
              },
              { ...newMatrixDays[dayOfWeek].driverDayLocks }
            );

            acc[dayOfWeek] = {
              ...newMatrixDays[dayOfWeek],
              routeDayLocks: newRouteDayLocks,
              driverDayLocks: newDriverDayLocks,
            };
          }
          return acc;
        },
        { ...matrixDays, ...newMatrixDays }
      );

      dispatch(
        addNotification({
          message: `Successfully saved matrix week changes.`,
          options: { variant: "success" },
        })
      );

      syncToAsync(() => dispatch(unlockDepotWeek()));
      return {
        newMatrixDays: newMatrixDaysWithUpdatedLocks,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message, error);
    }
  }
);

export const saveChanges = createAsyncThunk(
  "driverManagement/saveChanges",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const driverEdit = driverEditSelector(state);
    const driverEditCopy = driverEditCopySelector(state);
    if (
      driverEdit.driverProfileEdit !== driverEditCopy.driverProfileEdit ||
      driverEdit.imageChanged
    )
      dispatch(saveDriverProfile());
    else if (driverEdit.driverDayEdits !== driverEditCopy.driverDayEdits)
      dispatch(saveMatrixWeek());
  }
);
